'use client';

import Link from 'next/link';
import { Camera, FileText } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { Project } from '@/types/project';
import {
  hasImageFile,
  isSystemReference,
  type ProjectReference,
} from '@/types/project-reference';
import { useProjectAngles } from '@/shared/api/hooks/use-project-angles';
import { useProjectReferences } from '@/features/project-reference/hooks/use-project-references';
import {
  SectionHeader,
  ScrollableCarousel,
  SectionPanel,
  RequiredIndicator,
  PANEL_HEIGHT,
} from './section-components';
import { GoalsQualityIndicator } from './GoalsQualityIndicator';

// Square Brief material tile edge — matches the carousel card height used by
// the sibling sections (PANEL_HEIGHT - 24), kept square per the shared spec.
const TILE_SIZE = PANEL_HEIGHT - 24;

interface ProjectGoalsSectionProps {
  project: Project;
  projectId: string;
}

/**
 * Full Brief section on the building page — same anatomy as Designs /
 * Estimates / Blueprints: SectionHeader (title + count + indicator + chevron
 * navigating to the Brief page) over a horizontal carousel of the brief
 * materials (home photos first, then brief documents), or an Estimates-style
 * empty panel inviting the first photo upload.
 */
export function ProjectGoalsSection({
  project,
  projectId,
}: ProjectGoalsSectionProps) {
  const t = useTranslations('project');
  const goalsHref = `/project/${projectId}/goals`;

  const goalsText = project.attributes.goals?.trim() || '';
  const hasGoals = goalsText.length > 0;
  // Brief now hosts the building's views (photos), so the orange required-dot is
  // the onboarding nudge for the two essential empty tasks: no view uploaded
  // yet, or an empty brief.
  const hasViews = (project.attributes.angles_count ?? 0) > 0;
  const score = project.attributes.goals_quality_score ?? null;
  const pending = !!project.attributes.is_goals_evaluation_pending;

  // Materials = home photos (angles) first, then brief documents (brief-section
  // references). Same query keys/fetchers as the Brief page, so caches are
  // shared and existing WS/purchase invalidation paths keep both in sync.
  const { data: angles = [] } = useProjectAngles(projectId);
  const { data: briefReferences = [] } = useProjectReferences(projectId, {
    section: 'brief',
  });
  const briefDocuments = briefReferences.filter(
    (ref) => !isSystemReference(ref)
  );
  const materialsCount = angles.length + briefDocuments.length;

  const indicator =
    hasViews && hasGoals ? (
      // Has views and a filled brief → GSM signal-strength of the brief.
      // Orange is reserved exclusively for the empty-task indicator.
      <GoalsQualityIndicator
        score={score}
        hasGoals
        pending={pending}
        size="sm"
      />
    ) : (
      <RequiredIndicator />
    );

  return (
    <div className="space-y-4">
      <SectionHeader
        title={t('projectGoals')}
        count={materialsCount > 0 ? materialsCount : undefined}
        contextContent={indicator}
        actions={[{ label: t('projectGoals'), href: goalsHref }]}
      />
      {materialsCount > 0 ? (
        <ScrollableCarousel>
          {angles.map((angle) => (
            <Link
              key={`angle-${angle.id}`}
              href={goalsHref}
              className="relative flex-shrink-0 rounded-lg overflow-hidden bg-foreground/5"
              style={{ width: TILE_SIZE, height: TILE_SIZE }}
            >
              {angle.attributes.file_url && (
                <img
                  src={angle.attributes.file_url}
                  alt={angle.attributes.name || ''}
                  className="w-full h-full object-cover"
                />
              )}
            </Link>
          ))}
          {briefDocuments.map((reference) => (
            <BriefDocumentTile
              key={`ref-${reference.id}`}
              reference={reference}
              href={goalsHref}
            />
          ))}
        </ScrollableCarousel>
      ) : (
        // Brand-accent border marks the Brief empty panel as the place to
        // start (empty state only, never on the filled carousel).
        <SectionPanel className="border-2 border-primary">
          <Link
            href={goalsHref}
            className="flex h-full w-full flex-col items-center justify-center gap-2"
          >
            <Camera
              className="h-9 w-9 text-muted-foreground/50"
              strokeWidth={1.5}
            />
            <span className="text-card-meta text-muted-foreground/70">
              {t('briefEmpty')}
            </span>
          </Link>
        </SectionPanel>
      )}
    </div>
  );
}

/**
 * Square carousel tile for a brief document. An image-backed document (e.g. a
 * satellite/cadastral map) shows the actual picture; a file document (PDF/txt/
 * md) mirrors the Brief page's doc tile look — file icon + name.
 */
function BriefDocumentTile({
  reference,
  href,
}: {
  reference: ProjectReference;
  href: string;
}) {
  const fileName =
    reference.attributes.metadata?.extraction?.document_title ||
    reference.attributes.metadata?.original_filename ||
    reference.attributes.original_filename ||
    '';
  const imageUrl = reference.attributes.file_url;

  return (
    <Link
      href={href}
      className="relative flex-shrink-0 rounded-lg overflow-hidden bg-foreground/5"
      style={{ width: TILE_SIZE, height: TILE_SIZE }}
    >
      {hasImageFile(reference) && imageUrl ? (
        <img
          src={imageUrl}
          alt={fileName}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-muted-foreground">
          <FileText className="h-14 w-14" />
          <span className="text-card-meta line-clamp-2 text-center">
            {fileName}
          </span>
        </div>
      )}
    </Link>
  );
}
